import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

export const ContactForm = ({ type = 'general', title, description }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    type: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitted(true);
    }, 500);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white border border-slate-200 rounded-sm p-8 text-center"
        data-testid="contact-form-success"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="font-heading font-bold text-2xl text-slate-900 mb-3">
          Mensagem enviada!
        </h3>
        <p className="text-slate-600 mb-6">
          Obrigado pelo seu interesse. Nossa equipe entrará em contato em até 24 horas úteis.
        </p>
        <Button
          onClick={() => {
            setIsSubmitted(false);
            setFormData({
              name: '',
              email: '',
              phone: '',
              company: '',
              message: '',
              type: '',
            });
          }}
          variant="outline"
          className="rounded-sm"
          data-testid="contact-form-reset"
        >
          Enviar nova mensagem
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-white border border-slate-200 rounded-sm p-8"
    >
      {title && (
        <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">
          {title}
        </h3>
      )}
      {description && (
        <p className="text-slate-600 mb-6">{description}</p>
      )}

      <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome completo *</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Seu nome"
              className="rounded-sm bg-slate-50 border-slate-200 focus:border-lokker-blue"
              data-testid="contact-input-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">E-mail *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="seu@email.com"
              className="rounded-sm bg-slate-50 border-slate-200 focus:border-lokker-blue"
              data-testid="contact-input-email"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Telefone *</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="(11) 99999-9999"
              className="rounded-sm bg-slate-50 border-slate-200 focus:border-lokker-blue"
              data-testid="contact-input-phone"
            />
          </div>
          {(type === 'condominio' || type === 'empresa') && (
            <div className="space-y-2">
              <Label htmlFor="company">
                {type === 'condominio' ? 'Nome do Condomínio *' : 'Nome da Empresa *'}
              </Label>
              <Input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                placeholder={type === 'condominio' ? 'Condomínio XYZ' : 'Empresa XYZ'}
                className="rounded-sm bg-slate-50 border-slate-200 focus:border-lokker-blue"
                data-testid="contact-input-company"
              />
            </div>
          )}
        </div>

        {type === 'empresa' && (
          <div className="space-y-2">
            <Label htmlFor="businessType">Tipo de negócio *</Label>
            <Select
              value={formData.type}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, type: value }))}
            >
              <SelectTrigger className="rounded-sm bg-slate-50 border-slate-200" data-testid="contact-select-type">
                <SelectValue placeholder="Selecione o tipo de negócio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="shopping">Shopping Center</SelectItem>
                <SelectItem value="academia">Academia</SelectItem>
                <SelectItem value="supermercado">Supermercado</SelectItem>
                <SelectItem value="centro-comercial">Centro Comercial</SelectItem>
                <SelectItem value="coworking">Coworking</SelectItem>
                <SelectItem value="outro">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="message">Mensagem</Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Conte-nos mais sobre sua necessidade..."
            rows={4}
            className="rounded-sm bg-slate-50 border-slate-200 focus:border-lokker-blue resize-none"
            data-testid="contact-input-message"
          />
        </div>

        <Button
          type="submit"
          className="w-full btn-primary py-4 rounded-sm flex items-center justify-center gap-2"
          data-testid="contact-submit-button"
        >
          <Send className="w-4 h-4" />
          Enviar Mensagem
        </Button>

        <p className="text-xs text-slate-500 text-center">
          Ao enviar, você concorda com nossa{' '}
          <a href="#" className="text-lokker-blue hover:underline">
            Política de Privacidade
          </a>
          .
        </p>
      </form>
    </motion.div>
  );
};

export default ContactForm;
